export const getMembershipTermLabel = (payment) => {
  if (payment?.term?.term_label) {
    return payment.term.term_label;
  }

  if (payment?.term?.valid_from && payment?.term?.valid_to) {
    return `${formatDate(payment.term.valid_from, {
      includeTime: false,
      separator: "/",
    })} - ${formatDate(payment.term.valid_to, {
      includeTime: false,
      separator: "/",
    })}`;
  }

  if (payment?._membership_year) {
    return `Legacy compatibility year ${payment._membership_year}`;
  }

  return "N/A";
};

export const getMembershipResolutionLabel = (payment) =>
  payment?.resolution_source || "N/A";

export const getLegacyMembershipYearLabel = (payment) =>
  payment?._membership_year ? String(payment._membership_year) : "N/A";

const LEGACY_COMPATIBILITY_STATES = new Set([
  "legacy_extension",
  "expired_legacy",
]);

export const shouldShowLegacyMembershipYear = (payment) =>
  Boolean(payment?._membership_year) &&
  (!payment?.term?.term_label ||
    LEGACY_COMPATIBILITY_STATES.has(payment?.resolution_state));

const MEMBERSHIP_PLAN_PREFIX = "membership_";
const NON_PAID_MEMBERSHIP_TYPES = new Set([
  "membership_trainee",
  "membership_complimentary",
]);

const normalizePlanType = (planType) =>
  typeof planType === "string" ? planType.trim() : "";

const isMembershipPlan = (payment) =>
  normalizePlanType(payment?.plan_type).startsWith(MEMBERSHIP_PLAN_PREFIX);

export const isCurrentActiveMembershipPayment = (payment) =>
  payment?.payment_status === "succeeded" &&
  payment?.is_active === true &&
  isMembershipPlan(payment);

export const isCurrentActivePaidMembership = (payment) =>
  isCurrentActiveMembershipPayment(payment) &&
  !NON_PAID_MEMBERSHIP_TYPES.has(normalizePlanType(payment?.plan_type));

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const NAIVE_DATE_TIME_RE =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/;
const HAS_TIMEZONE_SUFFIX_RE = /(Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Format a date string to a specified format.
 * @param {string} dateStr - ISO date string
 * @param {object} [options]
 * @param {string} [options.separator="-"] - Date-part separator
 * @param {boolean} [options.includeSeconds=false] - Whether to include :ss
 * @param {boolean} [options.includeTime=true] - Whether to include HH:mm
 * @returns {string}
 */
export const formatDate = (
  dateStr,
  { separator = "-", includeSeconds = false, includeTime = true } = {},
) => {
  if (!dateStr) {
    return "N/A";
  }

  if (typeof dateStr === "string") {
    const trimmed = dateStr.trim();
    const dateOnlyMatch = trimmed.match(DATE_ONLY_RE);
    if (dateOnlyMatch) {
      const [, Y, M, D] = dateOnlyMatch;

      return `${Y}${separator}${M}${separator}${D}`;
    }

    const naiveDateTimeMatch =
      !HAS_TIMEZONE_SUFFIX_RE.test(trimmed) && trimmed.match(NAIVE_DATE_TIME_RE);
    if (naiveDateTimeMatch) {
      const [, Y, M, D, h, m, s = "00"] = naiveDateTimeMatch;
      const datePart = `${Y}${separator}${M}${separator}${D}`;

      if (!includeTime) {
        return datePart;
      }

      return `${datePart} ${h}:${m}${includeSeconds ? `:${s}` : ""}`;
    }
  }

  const d = new Date(dateStr);
  if (isNaN(d.getTime())) {
    return dateStr;
  }

  const Y = d.getFullYear();
  const M = String(d.getMonth() + 1).padStart(2, "0");
  const D = String(d.getDate()).padStart(2, "0");
  const datePart = `${Y}${separator}${M}${separator}${D}`;
  if (!includeTime) {
    return datePart;
  }

  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");

  return `${datePart} ${h}:${m}${includeSeconds ? `:${s}` : ""}`;
};
